import {SearchList} from '../../models/search-list.model';
import {FormState} from '../../models/form-state.model';
import {SearchOrder} from '../../enums/search-order.enum';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {catchError, map, tap} from 'rxjs/operators';
import {handleFetchListPage} from '../../helpers/search.helper';
import {OceanPolicyRegistryItem, SimplifiedOceanPolicy} from '../../models/simplified-ocean-policy.model';
import {RepositoryPoliciesService} from '../../services/repository-policies.service';
import {RepositoryPoliciesActions} from './repository-policies.actions';
import {AsideService} from '../../services/aside.service';
import {
    RepositoryPolicyTerminationAsideComponent
} from '../../modules/repository/pages/repository-policy-view/components/asides/repository-policy-termination-aside';
import {
    RepositoryPolicyChangeAsideComponent
} from '../../modules/repository/pages/repository-policy-view/components/asides/repository-policy-change-aside';
import {Claims} from '../../models/claims.model';
import {HttpErrorResponse} from '@angular/common/http';
import {throwError} from 'rxjs';
import {ClaimsService} from '../../services/claims.service';
import {TranslateService} from '@ngx-translate/core';
import {ModalService} from '../../services/modal.service';
import {FileService} from '../../services/file.service';
import {
    RepositoryPolicyDocumentsAsideComponent
} from '../../modules/repository/pages/repository-policy-view/components/asides/repository-policy-documents-aside';

interface RepositoryPoliciesStateModel {
    activeRepositoryPolicy: SimplifiedOceanPolicy;
    activeVersion: string;
    list: SearchList<OceanPolicyRegistryItem>;
    claims: Claims;
}

const createSearchList = () => new SearchList<OceanPolicyRegistryItem>({
    filters: new FormState(),
    orderByField: 'number',
    orderDirection: SearchOrder.DESC,
});

@State<RepositoryPoliciesStateModel>({
    name: RepositoryPoliciesState.STATE_NAME,
    defaults: {
        activeRepositoryPolicy: null,
        activeVersion: '1',
        list: createSearchList(),
        claims: null
    }
})

@Injectable()
export class RepositoryPoliciesState {
    static readonly STATE_NAME = 'repositoryPolicies';

    constructor(
        private store: Store,
        private repositoryPoliciesService: RepositoryPoliciesService,
        private asideService: AsideService,
        private claimsService: ClaimsService,
        private translateService: TranslateService,
        private modalService: ModalService,
        private fileService: FileService,
    ) {
    }

    @Selector()
    static activeRepositoryPolicy(state) {
        return state.activeRepositoryPolicy;
    }

    @Selector()
    static repositoryPoliciesRegistryList(state) {
        return state.list;
    }

    @Selector()
    static repositoryPoliciesRegistryFilters(state) {
        return state.list.filters.model;
    }

    @Selector()
    static repositoryPoliciesActiveVersion(state) {
        return state.activeVersion;
    }

    @Selector()
    static repositoryPoliciesClaims(state) {
        return state.claims;
    }

    @Action(RepositoryPoliciesActions.GetRepositoryPolicyById)
    getById(ctx: StateContext<RepositoryPoliciesStateModel>, {id}: RepositoryPoliciesActions.GetRepositoryPolicyById) {
        return this.repositoryPoliciesService.getRepositoryPolicy(id).pipe(
            tap((policy) => {
                ctx.patchState({activeRepositoryPolicy: new SimplifiedOceanPolicy(policy)});
                ctx.patchState({activeVersion: policy.history.length.toString()});
            })
        );
    }

    @Action(RepositoryPoliciesActions.GetListPage)
    getListPage(ctx: StateContext<RepositoryPoliciesStateModel>, action: RepositoryPoliciesActions.GetListPage) {
        return handleFetchListPage(ctx, action.params, (props) => this.repositoryPoliciesService.getRepositoryPoliciesList(props));
    }

    @Action(RepositoryPoliciesActions.GetRepositoryPolicyByIdHistoricVersion)
    getByIdHistoricVersion(
        ctx: StateContext<RepositoryPoliciesStateModel>,
        {id, version}: RepositoryPoliciesActions.GetRepositoryPolicyByIdHistoricVersion)
    {
        return this.repositoryPoliciesService.getRepositoryPolicyHistoricVersion(id, version).pipe(
            tap((policy) => {
                const history = ctx.getState().activeRepositoryPolicy.history;
                ctx.patchState({activeRepositoryPolicy: new SimplifiedOceanPolicy({...policy, history})});
                ctx.patchState({activeVersion: version.toString()});
            })
        );
    }

    @Action(RepositoryPoliciesActions.DownloadPolicyPrintout)
    downloadPolicyPrintout(ctx: StateContext<RepositoryPoliciesStateModel>, {policyId}: RepositoryPoliciesActions.DownloadPolicyPrintout) {
        return this.repositoryPoliciesService.downloadPrintout(policyId)
            .pipe(
                map(({token}) => this.fileService.getFileByToken(token))
            );

    }

    @Action(RepositoryPoliciesActions.DownloadFile)
    downloadFile(ctx: StateContext<RepositoryPoliciesStateModel>, {hash}: RepositoryPoliciesActions.DownloadFile) {
        return this.fileService.getTokenByHash(hash);
    }

    @Action(RepositoryPoliciesActions.OpenTerminationAside)
    openTerminationAside() {
        this.asideService.open(RepositoryPolicyTerminationAsideComponent);
    }

    @Action(RepositoryPoliciesActions.OpenDataChangeAside)
    openPersonalDataChangeAside() {
        this.asideService.open(RepositoryPolicyChangeAsideComponent);
    }

    @Action(RepositoryPoliciesActions.OpenDocumentsAside)
    openDocumentsAside() {
        this.asideService.open(RepositoryPolicyDocumentsAsideComponent);
    }

    @Action(RepositoryPoliciesActions.CloseAside)
    closeAside() {
        this.asideService.closeAside();
    }

    @Action(RepositoryPoliciesActions.GetInstallmentsAndClaims)
    getInstallmentsAndClaims(ctx: StateContext<RepositoryPoliciesStateModel>) {
        const policyId = ctx.getState().activeRepositoryPolicy.policyNumber;
        return this.claimsService.getRepositoryPolicyInstallmentsAndClaims(+policyId).pipe(
            tap((claims) => {
                    ctx.patchState({claims: new Claims(claims)});
                }
            ),
            catchError((error: HttpErrorResponse) => {
                if (error.status === 404) {
                    this.modalService.error({
                        nzContent: this.translateService.instant('LABELS.CLAIMS_NOT_FOUND')
                    });
                }
                return throwError(error);
            })
        );
    }

    @Action(RepositoryPoliciesActions.ClearListItems)
    clearListItems(ctx: StateContext<RepositoryPoliciesStateModel>) {
        const {list} = ctx.getState();
        ctx.patchState({
            list: {
                ...list,
                items: null
            }
        });
    }

    @Action(RepositoryPoliciesActions.ClearList)
    clearList(ctx: StateContext<RepositoryPoliciesStateModel>) {
        ctx.patchState({
            list: createSearchList()
        });
    }

    @Action(RepositoryPoliciesActions.Clear)
    clear(ctx: StateContext<RepositoryPoliciesStateModel>) {
        ctx.patchState({
            activeRepositoryPolicy: null,
            claims: null
        });
    }
}
